import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { emailValidation } from 'validators';
import { Button, TextField, Typography, useIsMounted } from 'seagull';
import { getTestIds } from 'seagull/utils/TestIds';
import commonStyles from '~/components/ChangeEmailAddressModal/ChangeEmailAddress.module.scss';
import { changeEmailErrorMessages, ViewType } from '~/components/ChangeEmailAddressModal/ChangeEmailAddressTypes';
import { useLocaleKeys } from '~/i18n';
import { ServerError } from '~/infra/ErrorResponse';
import { handleRequest } from '~/infra/utils';
import { useStoreActions } from '~/store/store-context';
import { connectControllerToTextField } from '~/utils/react-hook-form.utils';
export const GetSetUpNewEmailTID = (parentTestId)=>getTestIds({
        parentTestId,
        deprecatedTestIds: {
            SetUpNewEmailTID: {
                Root: 'set-up-new-email-address-view',
                InfoText: 'set-up-new-email-info-text',
                EmailInput: 'set-up-new-email-address',
                CancelButton: 'set-up-new-email-cancel-button',
                SendCodeButton: 'set-up-new-email-send-code-button'
            }
        }
    });
const SetUpNewEmail = (param)=>{
    let { onClose, handleSetView, handleSetNewEmail } = param;
    const { t: changeEmailModalTexts } = useLocaleKeys('components.changeEmailModal');
    const { api } = useStoreActions();
    const { SetUpNewEmailTID } = useMemo(()=>GetSetUpNewEmailTID(), []);
    const [isLoading, setIsLoading] = useState(false);
    const isMounted = useIsMounted();
    const { control, formState, handleSubmit, setError } = useForm({
        mode: 'onChange',
        defaultValues: {
            newEmail: ''
        }
    });
    const handleSetUpNewEmail = async (newEmail)=>{
        handleSetView(ViewType.VerifyNewEmail);
        handleSetNewEmail(newEmail);
        setIsLoading(false);
        return;
    };
    const handleError = (error)=>{
        if (error.hasServerError(ServerError.EmailAlreadyRegistered)) {
            setError('newEmail', {
                message: changeEmailErrorMessages.emailAlreadyRegistered
            });
            return;
        }
        if (error.isTooManyAttemptsError()) {
            handleSetView(ViewType.TooManyAttempts);
            return;
        }
        setError('newEmail', {
            message: changeEmailErrorMessages.somethingWentWrong
        });
    };
    const sendNewEmail = async (param)=>{
        let { newEmail } = param;
        setIsLoading(true);
        const changeEmailResult = await handleRequest(api.user.changeUserEmail({
            email: newEmail
        }));
        if (changeEmailResult.success) {
            await handleSetUpNewEmail(newEmail);
        } else {
            handleError(changeEmailResult.error);
        }
        if (isMounted()) {
            setIsLoading(false);
        }
    };
    return /*#__PURE__*/ _jsx("div", {
        "data-testid": SetUpNewEmailTID.Root(),
        children: /*#__PURE__*/ _jsxs("form", {
            onSubmit: handleSubmit(sendNewEmail),
            children: [
                /*#__PURE__*/ _jsxs("div", {
                    className: commonStyles.content,
                    children: [
                        /*#__PURE__*/ _jsx(Typography, {
                            variant: "paragraph1",
                            testId: SetUpNewEmailTID.InfoText(),
                            children: changeEmailModalTexts.verifyInfo()
                        }),
                        /*#__PURE__*/ _jsx(Controller, {
                            control: control,
                            name: "newEmail",
                            rules: emailValidation(undefined, {
                                required: true
                            }),
                            render: (p)=>/*#__PURE__*/ _jsx(TextField, _object_spread({
                                    bi: {
                                        name: 'new_email'
                                    },
                                    collapseHelperText: false,
                                    width: 360,
                                    mobile: {
                                        fluid: true
                                    },
                                    testId: SetUpNewEmailTID.EmailInput(),
                                    type: "email",
                                    label: changeEmailModalTexts.newEmailLabel()
                                }, connectControllerToTextField(p)))
                        })
                    ]
                }),
                /*#__PURE__*/ _jsxs("div", {
                    className: commonStyles.footer,
                    children: [
                        /*#__PURE__*/ _jsx(Button, {
                            bi: {
                                name: 'cancel_set_up_new_email'
                            },
                            onClick: onClose,
                            skin: "primaryOutlineOpaque",
                            testId: SetUpNewEmailTID.CancelButton(),
                            children: changeEmailModalTexts.cancelButton()
                        }),
                        /*#__PURE__*/ _jsx(Button, {
                            bi: {
                                name: 'send_set_up_new_email_code'
                            },
                            type: "submit",
                            testId: SetUpNewEmailTID.SendCodeButton(),
                            disabled: !formState.isValid,
                            loading: isLoading,
                            children: changeEmailModalTexts.sendCodeButton()
                        })
                    ]
                })
            ]
        })
    });
};
export default SetUpNewEmail;
